/**
 * The per-user event stream (GET /notifications/stream): new notifications,
 * unread counts, chat titles and operation status, over one long-lived SSE
 * connection per signed-in user (backend/src/schemas/user_events.py).
 *
 *   for await (const event of streamUserEvents({ signal })) { ... }
 *
 * Unlike an operation stream it has no end: it reconnects forever with
 * backoff (capped at a minute) until the signal aborts. Every connection
 * starts with a `heartbeat` whose status is "connected", so consumers can
 * refetch whatever they may have missed while disconnected.
 */

import { createParser } from "eventsource-parser";
import { reportError } from "@/telemetry/telemetry";
import { ApiError } from "./errors";
import type { UserStreamEvent } from "./generated/types.gen";
import { zUserStreamEvent } from "./generated/zod.gen";
import { getAccessToken } from "./tokens";

export type UserEvent = UserStreamEvent;
export type UserEventName = UserStreamEvent["event"];
export type UserEventOf<K extends UserEventName> = Extract<UserStreamEvent, { event: K }>;

export interface UserStreamOptions {
  signal?: AbortSignal;
  /** Base backoff in ms (doubles per failed attempt, capped at maxDelayMs). */
  retryDelayMs?: number;
  maxDelayMs?: number;
  /** Test seam (just the call signature). */
  fetchImpl?: (input: URL, init: RequestInit) => Promise<Response>;
}

export function toUserEvent(frame: { event?: string; data: string }): UserEvent | null {
  let data: unknown;
  try {
    data = JSON.parse(frame.data);
  } catch {
    reportError(new Error("Unparseable user stream frame"), { source: "userStream", event: frame.event ?? "message" });
    return null;
  }
  const parsed = zUserStreamEvent.safeParse({ event: frame.event ?? "message", data });
  if (!parsed.success) {
    reportError(new Error(`Unknown or malformed user stream event "${frame.event}"`), { source: "userStream" });
    return null;
  }
  return parsed.data as UserEvent;
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

type FetchImpl = NonNullable<UserStreamOptions["fetchImpl"]>;

async function open(fetchImpl: FetchImpl, signal?: AbortSignal): Promise<Response> {
  const url = new URL("/api/v1/notifications/stream", globalThis.location.origin);
  const token = await getAccessToken();
  let response: Response;
  try {
    response = await fetchImpl(url, {
      headers: { Accept: "text/event-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      signal,
    });
  } catch (error) {
    throw new ApiError(0, error instanceof Error ? error.message : "Network error");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, response.statusText, body, response.headers.get("x-request-id"));
  }
  return response;
}

/** Yield one connection's events until it ends or breaks. */
async function* readConnection(response: Response): AsyncGenerator<UserEvent> {
  const queue: UserEvent[] = [];
  const parser = createParser({
    onEvent: (frame) => {
      const event = toUserEvent(frame);
      if (event) queue.push(event);
    },
  });
  const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader();
  if (!reader) return;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(value);
      while (queue.length) yield queue.shift() as UserEvent;
    }
  } catch (error) {
    if (!(error instanceof TypeError || error instanceof DOMException)) throw error;
  } finally {
    reader.releaseLock();
  }
}

export async function* streamUserEvents(options: UserStreamOptions = {}): AsyncGenerator<UserEvent> {
  const { signal, retryDelayMs = 2000, maxDelayMs = 60_000, fetchImpl = fetch } = options;
  let attempt = 0;
  while (!signal?.aborted) {
    try {
      const response = await open(fetchImpl, signal);
      for await (const event of readConnection(response)) {
        attempt = 0; // progress resets the backoff
        yield event;
      }
    } catch (error) {
      if (signal?.aborted) return;
      // Client errors won't fix themselves soon: retry, but at the slowest pace.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) attempt = 64;
    }
    if (signal?.aborted) return;
    attempt += 1;
    // Sequential by design: each reconnect waits for the previous attempt's backoff.
    await sleep(Math.min(retryDelayMs * 2 ** (attempt - 1), maxDelayMs), signal); // NOSONAR
  }
}
